import React, { useState, useMemo, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';

const Subjects = () => {
    // State Management
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [subjects, setSubjects] = useState([]);
    
    // Form States
    const [formData, setFormData] = useState({
        fullName: '',
        shortName: '',
        numberOfClasses: 0,
    });
    
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Search and Filter Logic
    const filteredSubjects = useMemo(() => {
        if (!searchQuery.trim()) return subjects;
        
        const query = searchQuery.toLowerCase();
        return subjects.filter(subject =>
            subject.fullName.toLowerCase().includes(query) ||
            subject.shortName.toLowerCase().includes(query)
        );
    }, [subjects, searchQuery]);

    // Stats Calculation
    const stats = useMemo(() => ({
        total: subjects.length,
        withClasses: subjects.filter((s) => s.numberOfClasses > 0).length,
        withoutClasses: subjects.filter((s) => s.numberOfClasses === 0).length,
    }), [subjects]);

    // Form Validation
    // Centralized base path for Subjects API (admin-prefixed after route grouping)
    const SUBJECTS_BASE = '/admin/subjects';

    // Load subjects from backend
    const fetchSubjects = async () => {
        try {
            const res = await axiosInstance.get(SUBJECTS_BASE);
            const data = res.data || {};
            const list = Array.isArray(data.subjects) ? data.subjects : Array.isArray(data) ? data : [];
            setSubjects(list);
        } catch (error) {
            console.error('Error fetching subjects:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to load subjects.',
                confirmButtonColor: '#ef4444',
            });
        }
    };

    useEffect(() => {
        fetchSubjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateForm = async (data, isEdit = false) => {
        const newErrors = {};

        // Full Name validation
        if (!data.fullName.trim()) {
            newErrors.fullName = 'Full name is required';
        } else if (data.fullName.length > 255) {
            newErrors.fullName = 'Full name must not exceed 255 characters';
        }

        // Short Name validation
        if (!data.shortName.trim()) {
            newErrors.shortName = 'Short name is required';
        } else if (data.shortName.length > 50) {
            newErrors.shortName = 'Short name must not exceed 50 characters';
        } else {
            // Check uniqueness via API
            try {
                const params = {
                    shortName: data.shortName,
                };
                if (isEdit && selectedSubject?.subjectId) {
                    params.excludeId = selectedSubject.subjectId;
                }

                const response = await axiosInstance.get(`${SUBJECTS_BASE}/check-short-name`, {
                    params,
                });

                if (response.data?.exists) {
                    newErrors.shortName = 'This short name is already in use';
                }
            } catch (error) {
                console.error('Error checking short name uniqueness:', error);
            }
        }

        // Number of Classes validation
        if (data.numberOfClasses < 0) {
            newErrors.numberOfClasses = 'Number of classes cannot be negative';
        } else if (data.numberOfClasses > 500) {
            newErrors.numberOfClasses = 'Number of classes cannot exceed 500';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Reset Form
    const resetForm = () => {
        setFormData({
            fullName: '',
            shortName: '',
            numberOfClasses: 0,
        });
        setErrors({});
        setSelectedSubject(null);
    };

    // Handle Input Change
    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? (value === '' ? 0 : parseInt(value)) : value,
        }));
        // Clear error for this field
        if (errors[name]) {
            const newErrors = { ...errors };
            delete newErrors[name];
            setErrors(newErrors);
        }
    };

    // Add Subject
    const handleAddSubject = () => {
        resetForm();
        setIsAddModalOpen(true);
    };

    const handleSubmitAdd = async (e) => {
        e.preventDefault();
        
        const isValid = await validateForm(formData);
        if (!isValid) return;

        try {
            setIsSubmitting(true);
            await axiosInstance.post(SUBJECTS_BASE, formData);
            await fetchSubjects();
            setIsAddModalOpen(false);
            resetForm();
            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Subject created successfully!',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            });
        } catch (error) {
            console.error('Error creating subject:', error);

            const serverErrors = error.response?.data?.errors;
            const message = Array.isArray(serverErrors)
                ? serverErrors.join('\n')
                : error.response?.data?.message || 'Failed to create subject. Please try again.';

            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: message,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 4000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Edit Subject
    const handleEditSubject = (subject) => {
        setSelectedSubject(subject);
        setFormData({
            fullName: subject.fullName,
            shortName: subject.shortName,
            numberOfClasses: subject.numberOfClasses,
        });
        setIsEditModalOpen(true);
    };

    const handleSubmitEdit = async (e) => {
        e.preventDefault();
        if (!selectedSubject) return;

        const isValid = await validateForm(formData, true);
        if (!isValid) return;

        try {
            setIsSubmitting(true);
            await axiosInstance.put(`${SUBJECTS_BASE}/${selectedSubject.subjectId}`, formData);
            await fetchSubjects();
            setIsEditModalOpen(false);
            resetForm();
            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Subject updated successfully!',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            });
        } catch (error) {
            console.error('Error updating subject:', error);

            const serverErrors = error.response?.data?.errors;
            const message = Array.isArray(serverErrors)
                ? serverErrors.join('\n')
                : error.response?.data?.message || 'Failed to update subject. Please try again.';

            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: message,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 4000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Delete Subject
    const handleDeleteSubject = (subject) => {
        Swal.fire({
            title: 'Are you sure?',
            html: `
                <p>You are about to delete:</p>
                <p><strong>${subject.fullName}</strong> (${subject.shortName})</p>
                <p class="text-sm text-gray-600 mt-2">This action cannot be undone!</p>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                axiosInstance
                    .delete(`${SUBJECTS_BASE}/${subject.subjectId}`)
                    .then(async () => {
                        await fetchSubjects();
                        Swal.fire({
                            icon: 'success',
                            title: 'Deleted!',
                            text: 'Subject has been deleted successfully.',
                            toast: true,
                            position: 'top-end',
                            showConfirmButton: false,
                            timer: 3000,
                            timerProgressBar: true,
                        });
                    })
                    .catch((error) => {
                        Swal.fire({
                            icon: 'error',
                            title: 'Cannot Delete',
                            text: (error && error.message) || 'This subject is being used and cannot be deleted.',
                            confirmButtonColor: '#3b82f6',
                        });
                    });
            }
        });
    };

    // View Details
    const handleViewDetails = (subject) => {
        setSelectedSubject(subject);
        setIsDetailsModalOpen(true);
    };

    // Close Modals
    const closeAddModal = () => {
        setIsAddModalOpen(false);
        resetForm();
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        resetForm();
    };

    const closeDetailsModal = () => {
        setIsDetailsModalOpen(false);
        setSelectedSubject(null);
    };

    return (
        <DashboardLayout>
            <div className="p-6">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Subjects Management</h1>
                    <p className="text-gray-600 mt-2">Manage all academic subjects and their configurations</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-blue-600 text-sm font-semibold">Total Subjects</p>
                                <p className="text-3xl font-bold text-blue-900">{stats.total}</p>
                            </div>
                            <div className="bg-blue-100 p-3 rounded-full">
                                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-green-600 text-sm font-semibold">With Classes</p>
                                <p className="text-3xl font-bold text-green-900">{stats.withClasses}</p>
                            </div>
                            <div className="bg-green-100 p-3 rounded-full">
                                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-amber-600 text-sm font-semibold">Without Classes</p>
                                <p className="text-3xl font-bold text-amber-900">{stats.withoutClasses}</p>
                            </div>
                            <div className="bg-amber-100 p-3 rounded-full">
                                <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions Bar */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1 max-w-md">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search by full name or short name..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <svg
                                    className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>
                        <button
                            onClick={handleAddSubject}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add New Subject
                        </button>
                    </div>
                </div>

                {/* Subjects Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Full Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Short Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Number of Classes
                                    </th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredSubjects.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-12 text-center">
                                            <div className="flex flex-col items-center justify-center text-gray-500">
                                                <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                                </svg>
                                                <p className="text-lg font-semibold">No subjects found</p>
                                                <p className="text-sm">
                                                    {searchQuery ? 'Try adjusting your search' : 'Get started by adding a new subject'}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredSubjects.map((subject) => (
                                        <tr key={subject.subjectId} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                #{subject.subjectId}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {subject.fullName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                                    {subject.shortName}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold">{subject.numberOfClasses}</span>
                                                    <span className="text-gray-500 text-xs">classes</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => handleViewDetails(subject)}
                                                        className="text-blue-600 hover:text-blue-900 transition-colors"
                                                        title="View Details"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditSubject(subject)}
                                                        className="text-green-600 hover:text-green-900 transition-colors"
                                                        title="Edit"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteSubject(subject)}
                                                        className="text-red-600 hover:text-red-900 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Table Footer with Count */}
                    {filteredSubjects.length > 0 && (
                        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                            <p className="text-sm text-gray-600">
                                Showing <span className="font-semibold">{filteredSubjects.length}</span> of{' '}
                                <span className="font-semibold">{subjects.length}</span> subjects
                            </p>
                        </div>
                    )}
                </div>

                {/* Add Subject Modal */}
                {isAddModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-2xl font-bold text-gray-900">Add New Subject</h2>
                                    <button
                                        onClick={closeAddModal}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <form onSubmit={handleSubmitAdd} className="p-6">
                                <div className="space-y-6">
                                    {/* Full Name */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Full Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                                errors.fullName ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., Database Management Systems"
                                        />
                                        {errors.fullName && (
                                            <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
                                        )}
                                    </div>

                                    {/* Short Name */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Short Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="shortName"
                                            value={formData.shortName}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                                errors.shortName ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., DBMS"
                                        />
                                        {errors.shortName && (
                                            <p className="mt-1 text-sm text-red-600">{errors.shortName}</p>
                                        )}
                                    </div>

                                    {/* Number of Classes */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Number of Classes
                                        </label>
                                        <input
                                            type="number"
                                            name="numberOfClasses"
                                            value={formData.numberOfClasses}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="500"
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                                errors.numberOfClasses ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="0"
                                        />
                                        {errors.numberOfClasses && (
                                            <p className="mt-1 text-sm text-red-600">{errors.numberOfClasses}</p>
                                        )}
                                        <p className="mt-1 text-xs text-gray-500">Expected number of classes for this subject (0-500)</p>
                                    </div>
                                </div>

                                {/* Form Actions */}
                                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={closeAddModal}
                                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-semibold transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? 'Creating...' : 'Create Subject'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Edit Subject Modal */}
                {isEditModalOpen && selectedSubject && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-2xl font-bold text-gray-900">Edit Subject</h2>
                                    <button
                                        onClick={closeEditModal}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <form onSubmit={handleSubmitEdit} className="p-6">
                                <div className="space-y-6">
                                    {/* Full Name */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Full Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                                errors.fullName ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., Database Management Systems"
                                        />
                                        {errors.fullName && (
                                            <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
                                        )}
                                    </div>

                                    {/* Short Name */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Short Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="shortName"
                                            value={formData.shortName}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                                errors.shortName ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., DBMS"
                                        />
                                        {errors.shortName && (
                                            <p className="mt-1 text-sm text-red-600">{errors.shortName}</p>
                                        )}
                                    </div>

                                    {/* Number of Classes */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Number of Classes
                                        </label>
                                        <input
                                            type="number"
                                            name="numberOfClasses"
                                            value={formData.numberOfClasses}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="500"
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                                errors.numberOfClasses ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="0"
                                        />
                                        {errors.numberOfClasses && (
                                            <p className="mt-1 text-sm text-red-600">{errors.numberOfClasses}</p>
                                        )}
                                        <p className="mt-1 text-xs text-gray-500">Expected number of classes for this subject (0-500)</p>
                                    </div>
                                </div>

                                {/* Form Actions */}
                                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={closeEditModal}
                                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-semibold transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? 'Updating...' : 'Update Subject'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* View Details Modal */}
                {isDetailsModalOpen && selectedSubject && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-2xl font-bold text-gray-900">Subject Details</h2>
                                    <button
                                        onClick={closeDetailsModal}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <div className="p-6">
                                <div className="space-y-6">
                                    {/* Subject ID */}
                                    <div className="flex items-start gap-4">
                                        <div className="w-1/3">
                                            <p className="text-sm font-semibold text-gray-600">Subject ID</p>
                                        </div>
                                        <div className="w-2/3">
                                            <p className="text-gray-900 font-medium">#{selectedSubject.subjectId}</p>
                                        </div>
                                    </div>

                                    {/* Full Name */}
                                    <div className="flex items-start gap-4">
                                        <div className="w-1/3">
                                            <p className="text-sm font-semibold text-gray-600">Full Name</p>
                                        </div>
                                        <div className="w-2/3">
                                            <p className="text-gray-900 font-medium">{selectedSubject.fullName}</p>
                                        </div>
                                    </div>

                                    {/* Short Name */}
                                    <div className="flex items-start gap-4">
                                        <div className="w-1/3">
                                            <p className="text-sm font-semibold text-gray-600">Short Name</p>
                                        </div>
                                        <div className="w-2/3">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                                                {selectedSubject.shortName}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Number of Classes */}
                                    <div className="flex items-start gap-4">
                                        <div className="w-1/3">
                                            <p className="text-sm font-semibold text-gray-600">Number of Classes</p>
                                        </div>
                                        <div className="w-2/3">
                                            <p className="text-gray-900 font-medium">
                                                {selectedSubject.numberOfClasses} {selectedSubject.numberOfClasses === 1 ? 'class' : 'classes'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Timestamps */}
                                    <div className="border-t border-gray-200 pt-4">
                                        <div className="space-y-3">
                                            <div className="flex items-start gap-4">
                                                <div className="w-1/3">
                                                    <p className="text-sm font-semibold text-gray-600">Created At</p>
                                                </div>
                                                <div className="w-2/3">
                                                    <p className="text-gray-700 text-sm">
                                                        {new Date(selectedSubject.createdAt).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-4">
                                                <div className="w-1/3">
                                                    <p className="text-sm font-semibold text-gray-600">Updated At</p>
                                                </div>
                                                <div className="w-2/3">
                                                    <p className="text-gray-700 text-sm">
                                                        {new Date(selectedSubject.updatedAt).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Modal Actions */}
                                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                                    <button
                                        onClick={() => {
                                            closeDetailsModal();
                                            handleEditSubject(selectedSubject);
                                        }}
                                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors"
                                    >
                                        Edit Subject
                                    </button>
                                    <button
                                        onClick={closeDetailsModal}
                                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-semibold transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default Subjects;
